<script>
const SCI = require('sci')

// Remove repetições consecutivas de routePrefix do início de relative.
// Necessário porque handleRoute recebe de goto o path que o React interno
// pede pra navegar, relativo a basePath — se esse relative já vier com
// routePrefix embutido (ex.: relative = '/new/new/xyz', routePrefix =
// '/new'), montar target = basePath + routePrefix + relative duplicaria o
// prefixo a cada chamada, crescendo sem limite (mesma classe de bug já
// corrigida no vue-router via stripDuplicatedNewPrefix em NewPortal.vue,
// mas este caminho escreve a URL direto via window.history.pushState, sem
// passar pelo router).
function stripDuplicatedRoutePrefix(relative, routePrefix) {
    if (!routePrefix) {
        return relative
    }
    let stripped = relative
    while (stripped === routePrefix || stripped.startsWith(routePrefix + '/')) {
        stripped = stripped.slice(routePrefix.length)
    }
    return stripped
}

export default {
    name: "ReactFrame",
    props: {
        path: {
            required: true
        },
        // height: 'auto' (default) é um sentinela — NÃO é o valor CSS 'auto'.
        // Sinaliza "nenhum valor explícito recebido" e ativa o preenchimento
        // de altura via JS (ready() / setupFillHeight), já que o container
        // real (.page-content) não é flex e não tem altura própria definida.
        // Qualquer outro valor (ex.: '540px') pula esse cálculo e é aplicado
        // como CSS comum via computed style(), sem nenhuma mudança de
        // comportamento.
        height: {
            default: 'auto'
        },
        marginTop: {
            default: '0'
        },
        // Prefixo de rota que o HOST (Laravel/vue-router) injeta antes de
        // basePath no momento do mount (ex.: '/new' em /portals/new/*).
        // Declarado pelo consumidor porque só ele conhece sua própria rota
        // (ver routes/portals/index.js) — usado por handleRoute pra montar
        // de volta o path completo quando o React interno pede navegação
        // via postMessage.
        routePrefix: {
            default: ''
        },
        deepLink: {
            type: Boolean,
            default: false
        },
        searchQuery: {
            type: String,
            default: ''
        },
    },
    data() {
        return {
            hash: this.simpleHash(this.path),

            initialPath: window.location.pathname
        }
    },
    ready() {
        const hash = this.hash
        const iframe = document.getElementById(this.hash);
        const fillModeActive = this.height === 'auto'
        const basePath = '/' + this.path

        const routePrefix = this.routePrefix
        if (fillModeActive) {
            const divBotton = document.getElementById('paddingBottomFixed')
            const pageContent = document.getElementById('pageContent')
            if (divBotton) {
                divBotton.style.paddingBottom = '0px'
                pageContent.classList.add('no-padding')
            }
        }
        // Não dá pra confiar em window.REACT_FRONT pro origin real do
        // conteúdo do iframe: em staging existe um redirect não documentado
        // neste repo (front-sci-staging.* -> sci-staging.*), então o origin
        // configurado diverge do origin que o browser realmente usa. O
        // origin confiável só existe em runtime, no event.origin da 1ª
        // mensagem que o próprio iframe mandar pro host. Até aprender esse
        // valor, '*' é o fallback aceitável aqui porque a mensagem (altura/
        // largura em px) não é sensível.
        let childOrigin = null
        const resolveTargetOrigin = () => childOrigin || '*'
        const dispatchMaxSize = this.createSizeDispatcher(iframe, resolveTargetOrigin)
        const navigationDebugEnabled = /(?:^|[?&])debugIframeMessages=1(?:&|$)/.test(window.location.search)
        const navigationDebugStorageKey = 'sci:react-frame:navigation-events'
        // Histórico de navegações reportadas pelo iframe, usado pra reenviar o
        // último evento de navegação ao React interno quando o usuário volta
        // via popstate (ver handleNavigation e o listener de 'popstate' abaixo).
        let pages = []
        let currentPage = 0

        function handlePopstate(event) {
            const lastNavigationEvent = pages[currentPage - 1]
            if (lastNavigationEvent) {
                iframe.contentWindow.postMessage({...lastNavigationEvent.data}, resolveTargetOrigin());
            }
        }
        this._onPopstate = handlePopstate
        window.addEventListener('popstate', handlePopstate);
        function handleResize(event) {
            const iframe = document.getElementById(hash);
            iframe.style.height = event.data.height + 'px'
        }
        function handleNavigation(event) {
            if (navigationDebugEnabled) {
                const events = JSON.parse(sessionStorage.getItem(navigationDebugStorageKey) || '[]')
                const navigationEvent = {
                    at: new Date().toISOString(),
                    origin: event.origin,
                    data: event.data
                }

                events.push(navigationEvent)
                sessionStorage.setItem(navigationDebugStorageKey, JSON.stringify(events.slice(-50)))
                console.info('[ReactFrame navigation]', navigationEvent)
            }

            pages.push(event)
            currentPage++;
            window.history.pushState(null, '', event.data.goto)
        }

        function handleRoute(event) {
            const goto = event.data.goto
            const relative = goto.startsWith(basePath) ? goto.slice(basePath.length) : goto
            const sanitizedRelative = stripDuplicatedRoutePrefix(relative, routePrefix)
            const target = basePath + routePrefix + sanitizedRelative
            window.history.pushState(null, '', target)
        }

        function handleIframeMessage(event) {
            if (event.source !== iframe.contentWindow) {
                return; // ignora mensagens que não vieram deste iframe especificamente
            }
            childOrigin = event.origin

            switch (event.data.event) {
                case 'resize':
                    handleResize(event);
                    break;
                case 'navigation':
                    handleNavigation(event)
                    break;
                case 'askMaxSize':
                    dispatchMaxSize(true)
                    break;
                case 'route':
                    handleRoute(event)
                    break;

            }
        }

        this._onMessage = handleIframeMessage
        window.addEventListener("message", handleIframeMessage, false);

        if (fillModeActive) {
            this._stopFillHeight = this.setupFillHeight(iframe);
        }
        this._resizeObserver = this.observeIframeSize(iframe, dispatchMaxSize);
        
    },
    destroyed() {
        if (this._stopFillHeight) {
            const divBotton = document.getElementById('paddingBottomFixed')
            const pageContent = document.getElementById('pageContent')
            if (divBotton) {
                divBotton.style.paddingBottom = '4%'
                pageContent.classList.remove('no-padding')
            }
            this._stopFillHeight();
        }
        this._resizeObserver.disconnect();
        window.removeEventListener('popstate', this._onPopstate);
        window.removeEventListener("message", this._onMessage, false);
    },
    methods: {

        simpleHash(str) {
            const alphabet = 'abcdefghijklmnopqrstuvwxyz';
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                hash = ((hash << 5) - hash) + str.charCodeAt(i);
                hash = hash & hash;
            }
            hash = hash >>> 0;
            let result = '';
            for (let i = 0; i < 8; i++) {
                result += alphabet[hash % 26];
                hash = Math.floor(hash / 26);
            }
            return result;
        },
        // Preenche a altura disponível na viewport via JS, já que o container
        // real (.page-content) não é flex/não tem altura própria (CSS
        // height:100%/flex não resolveriam nada aqui). Roda só quando height
        // === 'auto' (sentinela, ver comentário na prop). Medido depois do
        // browser já ter aplicado marginTop, então compensa isso de graça.
        setupFillHeight(iframe) {
            const updateFillHeight = () => {
                const available = window.innerHeight - iframe.getBoundingClientRect().top;
                iframe.style.height = Math.max(0, available) + 'px';
            };
            updateFillHeight();
            window.addEventListener('resize', updateFillHeight);
            return () => window.removeEventListener('resize', updateFillHeight);
        },
        // Mede o iframe (getBoundingClientRect, funciona tanto vindo do
        // ResizeObserver quanto do evento 'load') e despacha 'maxSize' via
        // postMessage. Dedupe por valor (lastMaxSize) evita reenvio quando o
        // tamanho não mudou; force=true ignora o dedupe (usado no load, ver
        // observeIframeSize).
        createSizeDispatcher(iframe, getTargetOrigin) {
            let lastMaxSize = null;
            return (force) => {
                const rect = iframe.getBoundingClientRect();
                const width = Math.round(rect.width);
                const height = Math.round(rect.height);
                const isSameSize = !force && lastMaxSize && lastMaxSize.width === width && lastMaxSize.height === height;
                if (isSameSize) {
                    return;
                }

                lastMaxSize = { width, height };

                iframe.contentWindow.postMessage({ event: 'maxSize', height, width }, getTargetOrigin());
            };
        },
        // Dedupe por valor sozinho não evita flood durante um resize contínuo
        // de janela: cada frame do ResizeObserver produz um px arredondado
        // diferente do anterior, então quase nunca bate igual ao último
        // enviado. Envolve `dispatch(false)` num trailing debounce; expõe
        // `.cancel()` pra limpar o timer pendente no cleanup.
        createDebouncedDispatch(dispatch, delay) {
            let timer = null;
            const debounced = () => {
                clearTimeout(timer);
                timer = setTimeout(() => dispatch(false), delay);
            };
            debounced.cancel = () => clearTimeout(timer);
            return debounced;
        },
        // Reporta o tamanho real do iframe pro React interno via postMessage,
        // independente de como a altura foi definida (setupFillHeight ou prop
        // explícita). Orquestra: ResizeObserver (debounced) + evento 'load'
        // (imediato, sem debounce nem dedupe — ver comentário abaixo).
        observeIframeSize(iframe, dispatch) {
            const DEBOUNCE_MS = 150;
            const debouncedDispatch = this.createDebouncedDispatch(dispatch, DEBOUNCE_MS);

            const observer = new ResizeObserver(() => debouncedDispatch());
            observer.observe(iframe);

            // O ResizeObserver já dispara no mount, mas isso acontece antes do
            // React de dentro do iframe montar e registrar o listener dele —
            // postMessage não enfileira mensagens sem destinatário pronto, então
            // essa 1ª tentativa quase sempre se perde. O evento nativo 'load' do
            // iframe é um sinal bem mais próximo de "o app de dentro já rodou seu
            // script" — força um reenvio aqui mesmo que o valor não tenha mudado
            // (bypassa o dedupe só desta vez), pra garantir que pelo menos uma
            // mensagem chegue depois que o filho já estiver ouvindo. Sem
            // debounce aqui: é uma tentativa única, não faz sentido atrasá-la.
            const handleLoad = () => dispatch(true);
            iframe.addEventListener('load', handleLoad);

            return {
                disconnect() {
                    observer.disconnect();
                    iframe.removeEventListener('load', handleLoad);
                    debouncedDispatch.cancel();
                }
            };
        }
    },
    computed: {
        url() {

            const basePath = '/' + this.path
            let iframePath = basePath
            if (this.deepLink) {
                const prefixedBase = basePath + this.routePrefix
                iframePath = this.initialPath.startsWith(prefixedBase)
                    ? basePath + this.initialPath.slice(prefixedBase.length)
                    : basePath
            }
            const query = this.searchQuery ? `&q=${encodeURIComponent(this.searchQuery)}` : ''
            return `${window.REACT_FRONT}#${iframePath}?token=${this.token}${query}`
        },
        token() {
            const user = SCI.get('user')
            return user.session.token
        },
        style() {
            return {
                height: this.height,
                'margin-top': this.marginTop,
                display: 'block'
            }
        }
    }
}
</script>

<template>
    <iframe v-if="hash" :id="hash" :src="url" width="100%" :style="style"></iframe>
</template>

<style>
.no-padding {
    padding: 0!important;
}

.no-padding #paddingBottomFixed {
    padding-right: 0px!important;
}

#first-row {
    margin-right: 0!important;
}
</style>



// WEBPACK FOOTER //
// ReactFrame.vue?75152b7d